import type { EchoflicksVideoSession } from "../types";
import type { MediaUrlSource } from "./useMediaUrl";

/**
 * Echo poster thumbnails.
 *
 * The dashboard indexer projects the poster the orchestrator generated for an
 * echo (`poster_storage_key`, a bucket-relative JPEG of ~1536×1152, ~250 KB)
 * onto every echo row. Lists sign that key like any other media and hand the
 * signed URL to Vercel's Image Optimization API, which resizes it to the
 * width the layout needs and re-encodes it as WebP/AVIF, so a page of 25
 * thumbnails costs tens of kilobytes instead of ~6 MB of full posters and
 * zero video decoders.
 */

/** Widths registered in vercel.json `images.sizes`; the optimizer rejects others. */
export const OPTIMIZED_IMAGE_WIDTHS = [96, 192, 384, 768] as const;
export type OptimizedImageWidth = (typeof OPTIMIZED_IMAGE_WIDTHS)[number];

export const DEFAULT_IMAGE_QUALITY = 75;

export type PosterSourceFields = Pick<
  EchoflicksVideoSession,
  "poster_storage_key" | "poster_storage_bucket" | "final_video_storage_bucket"
>;

/** The poster as a signable media source, or null when the echo has none. */
export function sessionPosterSource(
  echo: PosterSourceFields,
): MediaUrlSource | null {
  if (!echo.poster_storage_key) return null;
  return {
    url: null,
    storage_bucket:
      echo.poster_storage_bucket ?? echo.final_video_storage_bucket ?? null,
    storage_path: echo.poster_storage_key,
    storage_is_public: false,
  };
}

/**
 * Only production builds run on Vercel, where `/_vercel/image` exists; local
 * dev, the demo and tests load the source image directly.
 */
export const isImageOptimizerAvailable = (): boolean =>
  import.meta.env.PROD === true;

export type OptimizeOptions = {
  quality?: number;
  /** Route through `/_vercel/image`; defaults to the build's availability. */
  enabled?: boolean;
};

/** `/_vercel/image?url=…&w=…&q=…` for an absolute https source, else the source itself. */
export function optimizedImageUrl(
  sourceUrl: string,
  width: OptimizedImageWidth,
  {
    quality = DEFAULT_IMAGE_QUALITY,
    enabled = isImageOptimizerAvailable(),
  }: OptimizeOptions = {},
): string {
  if (!enabled || !/^https:\/\//i.test(sourceUrl)) return sourceUrl;
  return `/_vercel/image?url=${encodeURIComponent(sourceUrl)}&w=${width}&q=${quality}`;
}

/** A `srcset` over several registered widths, for device-pixel-ratio aware `<img>`s. */
export function optimizedSrcSet(
  sourceUrl: string,
  widths: readonly OptimizedImageWidth[],
  options: OptimizeOptions = {},
): string {
  return widths
    .map((width) => `${optimizedImageUrl(sourceUrl, width, options)} ${width}w`)
    .join(", ");
}
